'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';
import type { SCAPIShopperProductsSchemas } from '@/types';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { EVENT_INSTANCE_ATTRIBUTE_ID } from '@/lib/event-variation';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';
import { formatEventDay, formatEventTimeRange } from './event-datetime';

interface EventOccurrenceSelectorProps {
    occurrences: SCAPIShopperProductsSchemas['Product'][];
    selectedProductId: string;
}

/** URL-driven session picker for Eventbrite series products. */
export default function EventOccurrenceSelector({
    occurrences,
    selectedProductId,
}: EventOccurrenceSelectorProps): ReactElement | null {
    const { t, i18n } = useTranslation('product');
    const commerceEnabled = useCommerceEnabled();
    const location = useLocation();
    const navigation = useNavigation();
    const pendingSelectedId =
        navigation.state !== 'idle' && navigation.location?.pathname === location.pathname
            ? new URLSearchParams(navigation.location.search).get(EVENT_INSTANCE_ATTRIBUTE_ID)
            : null;
    const effectiveSelectedId = pendingSelectedId || selectedProductId;
    const visibleOccurrences = occurrences.filter(
        (occurrence) =>
            !commerceEnabled || occurrence.inventory?.orderable !== false || occurrence.id === effectiveSelectedId
    );

    if (occurrences.length <= 1 || visibleOccurrences.length === 0) {
        return null;
    }

    return (
        <div
            className="flex w-full min-w-0 gap-2 overflow-x-auto pb-2 overscroll-x-contain scroll-smooth snap-x snap-mandatory touch-pan-x [scrollbar-width:thin] [scrollbar-color:var(--brown-10)_var(--sand-90)] [&::-webkit-scrollbar]:h-0.5 [&::-webkit-scrollbar-track]:bg-sand-90 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-brown-10"
            role="radiogroup"
            aria-label={t('eventCourse.datesAriaLabel')}
            data-testid="event-occurrence-selector">
            {visibleOccurrences.map((occurrence) => {
                const eventDay = formatEventDay(occurrence.c_eventStartDate, i18n.language);
                const eventTime = formatEventTimeRange(
                    occurrence.c_eventStartDate,
                    occurrence.c_eventEndDate,
                    i18n.language
                );
                const isSelected = occurrence.id === effectiveSelectedId;
                const isOrderable = occurrence.inventory?.orderable !== false;
                const availabilityLabel =
                    occurrence.inventory && !isOrderable
                        ? t(commerceEnabled ? 'eventCourse.soldOut' : 'eventCourse.ticketsUnavailable')
                        : undefined;
                const className = cn(
                    'flex min-w-[160px] flex-1 snap-start flex-col rounded-md border bg-neutral-white p-4 text-left',
                    isSelected ? 'border-brown-20' : 'border-neutral-80',
                    commerceEnabled && !isOrderable && 'cursor-not-allowed opacity-60'
                );
                const content = (
                    <>
                        {eventDay && <span className="body-copy-m-bold capitalize text-neutral-10">{eventDay}</span>}
                        {eventTime && <span className="body-copy-m text-neutral-40">{eventTime}</span>}
                        {availabilityLabel && (
                            <span className="body-copy-s mt-2 text-status-green-40">{availabilityLabel}</span>
                        )}
                    </>
                );

                if (commerceEnabled && !isOrderable) {
                    return (
                        <span
                            key={occurrence.id}
                            role="radio"
                            aria-checked={isSelected}
                            aria-disabled="true"
                            className={className}>
                            {content}
                        </span>
                    );
                }

                const params = new URLSearchParams(location.search);
                params.set(EVENT_INSTANCE_ATTRIBUTE_ID, occurrence.id);
                params.delete('pid');

                return (
                    <Link
                        key={occurrence.id}
                        to={`${location.pathname}?${params.toString()}`}
                        preventScrollReset
                        role="radio"
                        aria-checked={isSelected}
                        className={className}>
                        {content}
                    </Link>
                );
            })}
        </div>
    );
}
